import {
	Component,
	ComponentInterface,
	Element,
	Listen,
	Prop,
	State,
} from '@stencil/core';
import { getCurrentBreakpoint } from '../_helpers/breakpoints';
import debounce from '../../../common/_helpers/debounce';
import {
	moveInlineStylesToHead,
	removeInlineStylesFromHead,
} from '../_helpers/content-security-policy';

@Component({
	tag: 'ds-fit-text',
	styles: `
        .ds-fit-text-content {
        	margin: 0;
            display: block;
            white-space: normal;
			width: fit-content;
        }
    `,
})
export class FitText implements ComponentInterface {
	@Element() el: HTMLDsFitTextElement;

	@State() maxFontSize = 78;
	@State() minFontSize = 56;
	@State() height = 0;

	@Prop() minLineHeight = 0.96;
	@Prop() maxLineHeight = 1.06;
	@Prop() minLetterSpacing = -1.9;
	@Prop() maxLetterSpacing = -1.2;

	@State() heightAndFontSizeMap = {
		XS: {
			height: 120,
			minFontSize: 40,
		},
		SM: {
			height: 90,
			minFontSize: 40,
		},
		MD: {
			height: 126,
			minFontSize: 40,
		},
		LG: {
			height: 144,
			minFontSize: 52,
		},
		XL: {
			height: 208,
			minFontSize: 56,
		},
	};

	private textElement: HTMLElement;

	@Listen('resize', { target: 'window', passive: false })
	windowResizeHandler() {
		this.scaleTextDebounced();
	}

	componentDidRender(): void {
		this.textElement = this.el.querySelector(
			'.ds-fit-text-content'
		) as HTMLElement;
	}

	componentDidLoad(): void {
		this.setNoImageMinFontSize();
		this.scaleText();
	}

	componentDidUpdate(): void {
		this.setNoImageMinFontSize();
		this.scaleText();
		moveInlineStylesToHead(this.el.id);
	}

	disconnectedCallback() {
		removeInlineStylesFromHead(this.el.id);
	}

	//  If the image is missing, set different headline min font size per breakpoint
	setNoImageMinFontSize(): void {
		const hasNoImage = Boolean(this.el.closest('.ds-sb-no-image'));

		if (hasNoImage) {
			this.heightAndFontSizeMap.XS.minFontSize = 38;
			this.heightAndFontSizeMap.SM.minFontSize = 44;
			this.heightAndFontSizeMap.MD.minFontSize = 56;
			this.heightAndFontSizeMap.LG.minFontSize = 60;
			this.heightAndFontSizeMap.XL.minFontSize = 82;
		}
	}

	// First to be called. Sets default parameters based on breakpoint.
	setComponentDefaultHeightAndFontSize(): void {
		const { height, minFontSize } =
			this.heightAndFontSizeMap[getCurrentBreakpoint()];
		this.height = height;
		this.minFontSize = minFontSize;
	}

	// Last to be called. Counts lines of inline span element, and reduces component height if there's extra space.
	shortenComponentHeight({ element, fontSize }) {
		const lines = element.children[0].getClientRects().length;
		const textHeight = lines * fontSize * element.style.lineHeight;

		if (textHeight < this.height) element.style.height = `${textHeight}px`;
	}

	// Lineary increases line height as font size get's smaller
	setLineHeight({ element, fontSize }) {
		const lineHeight =
			((this.maxFontSize - fontSize) / (this.maxFontSize - this.minFontSize)) *
				(this.maxLineHeight - this.minLineHeight) +
			this.minLineHeight;
		element.style.lineHeight = lineHeight;
	}

	// Lineary increases letter spacing as font size get's smaller
	setLetterSpacing({ element, fontSize }) {
		const letterSpacing =
			((fontSize - this.minFontSize) / (this.maxFontSize - this.minFontSize)) *
				(this.minLetterSpacing - this.maxLetterSpacing) +
			this.maxLetterSpacing;

		element.style.letterSpacing = `${letterSpacing}px`;
	}

	// Recursive function that checks for overflow and if true runs again with fontSize reduced by 1.
	handleOverflow({ element, container, fontSize, minFontSize, height }) {
		this.setLineHeight({ element, fontSize });
		this.setLetterSpacing({ element, fontSize });

		element.style.fontSize = `${fontSize}px`;
		element.style.height = `${height}px`;
		element.style.wordBreak = 'normal';

		if (fontSize === minFontSize) {
			element.style.height = 'auto';
			element.style.wordBreak = 'break-word';
			return;
		}

		const overflow =
			element.scrollHeight > element.clientHeight ||
			element.scrollWidth > element.clientWidth;

		if (overflow) {
			this.handleOverflow({
				element,
				container,
				fontSize: fontSize - 1,
				minFontSize,
				height,
			});
		} else {
			element.style.fontSize = `${fontSize}px`;
			this.shortenComponentHeight({ element, fontSize });
		}
	}

	scaleText = () => {
		this.setComponentDefaultHeightAndFontSize();
		if (this.textElement)
			this.handleOverflow({
				element: this.textElement,
				container: this.el,
				fontSize: this.maxFontSize,
				minFontSize: this.minFontSize,
				height: this.height,
			});
	};
	scaleTextDebounced = debounce(this.scaleText, 10);

	[memberName: string]: any;
}
